import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '../lib/utils.js';

export const statusBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
  {
    variants: {
      variant: {
        neutral: 'bg-muted text-muted-foreground border-border',
        info: 'bg-accent/10 text-accent border-accent/24',
        success: 'bg-success/10 text-success border-success/24',
        warning: 'bg-warning/10 text-warning border-warning/30',
        destructive: 'bg-destructive/10 text-destructive border-destructive/30',
      },
      dot: {
        true: 'pl-1.5',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'neutral',
      dot: false,
    },
  },
);

const dotColors = {
  neutral: 'bg-muted-foreground',
  info: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
} as const;

export interface StatusBadgeProps
  extends ComponentProps<'span'>,
    VariantProps<typeof statusBadgeVariants> {}

export function StatusBadge({
  variant = 'neutral',
  dot = false,
  className,
  children,
  ...props
}: Readonly<StatusBadgeProps>) {
  return (
    <span
      data-slot="status-badge"
      className={cn(statusBadgeVariants({ variant, dot }), className)}
      {...props}
    >
      {dot === true && (
        <span
          className={cn('size-1.5 rounded-full', dotColors[variant ?? 'neutral'])}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}
